import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@ophelos/ui/components/chart';
import { Area, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import { formatMoneyCompact } from '@/lib/currency';

export interface PlanBalancePoint {
  month: string;
  /** Balance after a payment that has actually cleared. */
  balance?: number;
  /** Balance the schedule projects, cleared months included so the two lines join. */
  predicted?: number;
}

interface PlanBalanceChartProps {
  data: PlanBalancePoint[];
  currency: string;
  className?: string;
}

/** The plan's balance stepping down month by month: solid where paid, projected where not. */
export function PlanBalanceChart({ data, currency, className }: PlanBalanceChartProps) {
  const { t } = useTranslation();
  const chartConfig = {
    balance: { label: t('paymentPlan.remainingBalance'), color: 'var(--chart-1)' },
    predicted: { label: t('paymentPlan.remainingBalance'), color: 'var(--chart-4)' },
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} className={className ?? 'h-full w-full'}>
      <ComposedChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
        <defs>
          <linearGradient id="plan-balance-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-balance)" stopOpacity={0.28} />
            <stop offset="100%" stopColor="var(--color-balance)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} />
        <YAxis domain={[0, 'auto']} tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(value) => formatMoneyCompact(value, currency)} />
        <ChartTooltip
          cursor={false}
          content={({ payload, ...props }) => {
            // A paid month carries both series at the same value, and both are labelled
            // "remaining balance" — show what was actually paid and drop the projection
            // that runs underneath it.
            const rows = (payload ?? []).filter((row) => row.value != null);
            const actual = rows.find((row) => row.dataKey === 'balance');
            return (
              <ChartTooltipContent
                {...props}
                payload={actual ? [actual] : rows}
                formatter={(value, name, item) => (
                  <>
                    <div className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: item.color }} />
                    <div className="flex flex-1 items-center justify-between gap-2 leading-none">
                      <span className="text-muted-foreground">{chartConfig[name as keyof typeof chartConfig]?.label ?? name}</span>
                      <CurrencyDisplay amount={Number(value)} currency={currency} className="text-foreground font-medium tabular-nums" />
                    </div>
                  </>
                )}
              />
            );
          }}
        />
        <Area type="stepAfter" dataKey="balance" stroke="none" fill="url(#plan-balance-fill)" isAnimationActive={false} tooltipType="none" />
        <Line type="stepAfter" dataKey="predicted" stroke="var(--color-predicted)" strokeWidth={2} dot={false} />
        <Line type="stepAfter" dataKey="balance" stroke="var(--color-balance)" strokeWidth={2} dot={false} />
      </ComposedChart>
    </ChartContainer>
  );
}
